import { NavLink } from 'react-router'
import { LayoutDashboard, Users, CheckSquare, UsersRound, LogOut, Sparkles } from 'lucide-react'
import { roleCanManage, roleCanBrowseClients } from '@shared/enums'
import { useSession, useSignOut } from '../../hooks/useAuth'

// Each item names the capability predicate that registers its route in
// App.tsx, so the nav and the router can't disagree. The Team page carries
// everyone's contact details, platform role and login state, so it's admin-only.
// Clients are open to the Team role as working hubs (LXP-55) — the API keeps
// financials, contracts and client edits behind admin.
type NavGate = 'all' | 'browseClients' | 'manage'
const NAV_ITEMS: { to: string; label: string; icon: typeof Users; gate: NavGate }[] = [
  { to: '/', label: 'Today', icon: LayoutDashboard, gate: 'all' },
  { to: '/clients', label: 'Clients', icon: Users, gate: 'browseClients' },
  { to: '/tasks', label: 'Tasks', icon: CheckSquare, gate: 'all' },
  { to: '/team', label: 'Team', icon: UsersRound, gate: 'manage' },
]

interface SidebarProps {
  /** Mobile drawer state — ignored on desktop where the sidebar is static. */
  open?: boolean
  /** Called when a nav choice is made, so the mobile drawer can close. */
  onNavigate?: () => void
  /** Opens the request-intake assistant panel. */
  onOpenAssistant?: () => void
}

export function Sidebar({ open = false, onNavigate, onOpenAssistant }: SidebarProps) {
  const signOut = useSignOut()
  const { data: session } = useSession()
  const role = (session?.user as { role?: string } | undefined)?.role ?? 'member'
  const allowed: Record<NavGate, boolean> = {
    all: true,
    browseClients: roleCanBrowseClients(role),
    manage: roleCanManage(role),
  }
  const navItems = NAV_ITEMS.filter((item) => allowed[item.gate])

  return (
    <aside className={`sidebar${open ? ' sidebar--open' : ''}`}>
      <div className="sidebar-brand">
        <div className="sidebar-brand-icon">×</div>
        <span className="sidebar-brand-text">
          double<span style={{ color: 'var(--accent-primary)' }}>·</span>xl
        </span>
        <span className="sidebar-brand-sub">Studio OS</span>
      </div>

      <nav className="sidebar-nav">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `sidebar-link${isActive ? ' sidebar-link--active' : ''}`
            }
            end={to === '/'}
            onClick={onNavigate}
          >
            <Icon size={18} strokeWidth={2} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer">
        {onOpenAssistant && (
          <button
            className="sidebar-link sidebar-link--assistant"
            onClick={onOpenAssistant}
            type="button"
          >
            <Sparkles size={18} strokeWidth={2} />
            <span>Report / request</span>
            <kbd className="sidebar-kbd">⌘I</kbd>
          </button>
        )}
        <button
          className="sidebar-link"
          onClick={() => signOut.mutate()}
          type="button"
        >
          <LogOut size={18} strokeWidth={2} />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  )
}
